import { useState, useRef, useEffect } from "react";
import BaseModal from "@components/modals/BaseModal";
import Icon1 from "@assets/icons/Icon";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import { dossiersApi } from "@services/api/dossiers";
import { ordresApi } from "@services/api/ordres";
import { vehiclesApi } from "@services/api/vehicles";
import { useOrdreStore } from "@store/ordreStore";
import { showNotification } from "context/notification.service";
import { TrackingContentV2 } from "@screens/TrackingScreenV2";
import { getTrackingData, type TrackingData } from "@services/api/tracking";
import type { Ordre } from "types/ordre";
import type { Dossier } from "types/dossier";
import type { Vehicle } from "types/garage/vehicules";

/**
 * Sub-modals opened from the ordre "Actions" menu. Extracted from
 * OrdreDetailScreen so both the Ordre page and the OrdreModal (opened from the
 * planning) can render them — see OrdreActionsMenu.
 */

const DOCUMENT_TYPE_OPTIONS = [
  { value: "carte_grise", label: "Carte grise" },
  { value: "assurance", label: "Assurance" },
  { value: "controle_technique", label: "Contrôle technique" },
  { value: "constat_amiable", label: "Constat amiable" },
  { value: "permis_conduire", label: "Permis de conduire" },
  { value: "devis", label: "Devis" },
  { value: "facture", label: "Facture" },
  { value: "bon_commande", label: "Bon de commande" },
  { value: "bon_livraison", label: "Bon de livraison" },
  { value: "contrat_location", label: "Contrat de location / prêt" },
  { value: "rapport_expertise", label: "Rapport d'expertise" },
  { value: "photo_vehicule", label: "Photo véhicule" },
  { value: "photo_degats", label: "Photo dégâts" },
  { value: "courrier", label: "Courrier" },
  { value: "autre", label: "Autre" },
];

const MOBILITY_LABELS: Record<string, string> = {
  attente: "Attente sur place",
  velo: "Vélo",
  pret: "Véhicule de prêt",
  location: "Véhicule de location",
};

const USAGE_FOR_MOBILITY: Record<string, string> = {
  velo: "velos",
  pret: "pret",
  location: "location",
};

// ─── Add document modal — uploads to parent dossier ──────────

export function AddDocumentModalOrdre({
  dossierId,
  onClose,
  onSuccess,
}: {
  dossierId: string;
  onClose: () => void;
  onSuccess: (dossier: Dossier) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [docName, setDocName] = useState("");
  const [docType, setDocType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setDocName(selectedFile.name);
    setClassifying(true);
    try {
      const result = await dossiersApi.classifyDocument(selectedFile);
      if (result.suggested_name) {
        const origExt = selectedFile.name.includes(".") ? selectedFile.name.substring(selectedFile.name.lastIndexOf(".")) : "";
        const suggestedHasExt = result.suggested_name.includes(".");
        setDocName(suggestedHasExt ? result.suggested_name : result.suggested_name + origExt);
      }
      if (result.type_key) setDocType(result.type_key);
    } catch { /* user can manually select */ }
    finally { setClassifying(false); }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      let uploadFile = file;
      if (file.type.startsWith("image/")) {
        const { compressImage } = await import("@utils/compressImage");
        uploadFile = await compressImage(file);
      }
      const updated = await dossiersApi.uploadDocument(dossierId, uploadFile, docName || undefined, docType || undefined);
      showNotification({ type: "success", message: "Document ajouté" });
      onSuccess(updated);
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'envoi" });
    } finally { setUploading(false); }
  };

  return (
    <BaseModal title="Ajouter un document" startIcon={<Icon1 name="upload" size={20} />} onClose={onClose} width="max-w-md">
      <div className="flex flex-col gap-4 p-4">
        <div
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${dragOver ? "border-indigo-400 bg-indigo-50" : "border-neutral-300 hover:bg-neutral-50"}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFileSelect(f); }}
        >
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <Icon1 name="file" size={28} color="#6366f1" />
              <span className="text-h3 font-medium text-neutral-700 text-center">{file.name}</span>
              <span className="text-h4 text-neutral-400">{(file.size / 1024).toFixed(0)} Ko</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Icon1 name="upload" size={24} color="#A1A1A1" />
              <span className="text-h3 text-neutral-400">Glisser ou cliquer pour importer</span>
              <span className="text-h4 text-neutral-300">PDF, images, documents</span>
            </div>
          )}
          <input ref={fileInputRef} type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }} />
        </div>
        {classifying && (
          <div className="flex flex-row items-center gap-2 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
            <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-h3 text-indigo-600">Analyse du document...</span>
          </div>
        )}
        <TextField label="Nom du document" value={docName} onChange={setDocName} />
        <DropDown label="Type de document" options={DOCUMENT_TYPE_OPTIONS} value={docType} onSelect={(v) => setDocType(String(v))} />
      </div>
      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
        <Button variant="small" label={uploading ? "Envoi..." : "Ajouter"} onClick={handleUpload} disabled={!file || uploading} />
      </div>
    </BaseModal>
  );
}

// ─── Facture upload modal — uploads to ordre documents ───────

export function FactureUploadModal({
  ordreId,
  onClose,
  onSuccess,
}: {
  ordreId: string;
  onClose: () => void;
  onSuccess: (ordre: Ordre) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [docName, setDocName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    setDocName(selectedFile.name);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const updated = await ordresApi.uploadDocument(ordreId, file, docName || "Facture", "facture");
      showNotification({ type: "success", message: "Facture ajoutée" });
      onSuccess(updated);
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'envoi" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <BaseModal title="Ajouter la facture" startIcon={<Icon1 name="file" size={20} />} onClose={onClose} width="max-w-md">
      <div className="flex flex-col gap-4 p-4">
        <div
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${dragOver ? "border-indigo-400 bg-indigo-50" : "border-neutral-300 hover:bg-neutral-50"}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFileSelect(f); }}
        >
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <Icon1 name="file" size={28} color="#6366f1" />
              <span className="text-h3 font-medium text-neutral-700 text-center">{file.name}</span>
              <span className="text-h4 text-neutral-400">{(file.size / 1024).toFixed(0)} Ko</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Icon1 name="upload" size={24} color="#A1A1A1" />
              <span className="text-h3 text-neutral-400">Glisser ou cliquer pour importer</span>
              <span className="text-h4 text-neutral-300">PDF ou image de la facture</span>
            </div>
          )}
          <input ref={fileInputRef} type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }} />
        </div>
        <TextField label="Nom du document" value={docName} onChange={setDocName} />
      </div>
      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
        <Button variant="small" label={uploading ? "Envoi..." : "Ajouter la facture"} onClick={handleUpload} disabled={!file || uploading} />
      </div>
    </BaseModal>
  );
}

// ─── Rental vehicle assignment modal ────────────────────────

export function RentalVehicleModal({
  ordre,
  orgId,
  onClose,
  onAssign,
}: {
  ordre: Ordre;
  orgId: string;
  onClose: () => void;
  onAssign: (vehicle: Vehicle, reservationId: string) => void;
}) {
  const [available, setAvailable] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState<string | null>(null);
  const matchingUsage = USAGE_FOR_MOBILITY[ordre.mobility_option || ""] || null;
  const [filterByRequest, setFilterByRequest] = useState(!!matchingUsage);

  const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
  const weekLaterStr = (() => { const d = new Date(); d.setDate(d.getDate() + 7); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();

  const startDate = ordre.reception_date
    ? (() => { const d = new Date(ordre.reception_date.seconds * 1000); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })()
    : todayStr;
  const endDate = ordre.restitution
    ? (() => { const d = new Date(ordre.restitution.seconds * 1000); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })()
    : startDate || weekLaterStr;

  const startHour = ordre.reception_date
    ? new Date(ordre.reception_date.seconds * 1000).getHours() + new Date(ordre.reception_date.seconds * 1000).getMinutes() / 60
    : undefined;
  const returnHour = ordre.restitution
    ? new Date(ordre.restitution.seconds * 1000).getHours() + new Date(ordre.restitution.seconds * 1000).getMinutes() / 60
    : undefined;

  useEffect(() => {
    vehiclesApi.available({
      org_id: orgId,
      start_date: startDate,
      end_date: endDate,
      start_hour: startDate === endDate ? startHour : undefined,
      end_hour: startDate === endDate ? returnHour : undefined,
    })
      .then((v) => { setAvailable(v); })
      .catch(() => { setAvailable([]); })
      .finally(() => setLoading(false));
  }, [orgId, startDate, endDate, startHour, returnHour]);

  const handleAssign = async (vehicle: Vehicle) => {
    if (!startDate || !endDate) return;
    setAssigning(vehicle.id);
    try {
      const startHour = ordre.reception_date
        ? new Date(ordre.reception_date.seconds * 1000).getHours() + new Date(ordre.reception_date.seconds * 1000).getMinutes() / 60
        : 8;
      const returnHour = ordre.restitution
        ? new Date(ordre.restitution.seconds * 1000).getHours() + new Date(ordre.restitution.seconds * 1000).getMinutes() / 60
        : 17;

      const clientName = ordre.name || ordre.contacts?.[0]?.nom || "Client";
      const reservation = await vehiclesApi.createReservation({
        org_id: orgId,
        vehicleId: vehicle.id,
        clientName,
        startDate,
        endDate,
        hasPickup: true,
        hasReturn: true,
        startHour,
        returnHour: startDate === endDate ? undefined : returnHour,
        durationHours: startDate === endDate ? (returnHour - startHour) : undefined,
        ordreId: ordre.id,
      });

      const updatedOrdre = await ordresApi.update(ordre.id, {
        rental_vehicle_id: vehicle.id,
        rental_reservation_id: reservation.id,
      });

      ordresApi.addVehicleBookingEvent(ordre.id, {
        vehicle_plate: vehicle.plate,
        vehicle_name: vehicle.name,
        start_date: startDate,
        end_date: endDate,
      }).catch((e) => console.error("Error adding booking timeline event:", e));

      useOrdreStore.getState().updateOrdre(updatedOrdre);
      onAssign(vehicle, reservation.id);
      showNotification({ type: "success", message: `${vehicle.name} (${vehicle.plate}) attribué` });
      onClose();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'attribution" });
    } finally {
      setAssigning(null);
    }
  };

  const usageLabel = MOBILITY_LABELS[ordre.mobility_option || ""] || "Véhicule de remplacement";
  const filteredVehicles = filterByRequest && matchingUsage
    ? available.filter((v) => v.usage === matchingUsage)
    : available;

  return (
    <BaseModal
      title="Attribuer un véhicule"
      startIcon={<Icon1 name="side-ordres" size={20} />}
      onClose={onClose}
      width="max-w-lg"
    >
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-col gap-1 p-3 bg-neutral-50 rounded-lg border border-neutral-200">
          <div className="flex flex-row items-center gap-2">
            <span className="text-h3 text-neutral-400">Demande client :</span>
            <span className="text-h3 font-medium text-black">{usageLabel}</span>
          </div>
          <div className="flex flex-row items-center gap-2">
            <span className="text-h3 text-neutral-400">Période :</span>
            <span className="text-h3 font-medium text-black">
              {startDate && endDate ? `${startDate} → ${endDate}` : "Dates non définies"}
            </span>
          </div>
        </div>

        {matchingUsage && (
          <label className="flex flex-row items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filterByRequest}
              onChange={() => setFilterByRequest(!filterByRequest)}
              className="w-4 h-4 accent-[#5C6BFF]"
            />
            <span className="text-h3 text-neutral-500">
              Afficher uniquement les {usageLabel.toLowerCase()}s
            </span>
            {filterByRequest && (
              <span className="text-h4 text-neutral-400">
                ({filteredVehicles.length}/{available.length})
              </span>
            )}
          </label>
        )}

        {!startDate || !endDate ? (
          <div className="text-h3 text-center text-neutral-400 py-6">
            Définissez les dates de réception et restitution pour voir les véhicules disponibles.
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-[#5C6BFF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredVehicles.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-6">
            <span className="text-h3 text-neutral-400">
              {available.length > 0 && filterByRequest
                ? `Aucun ${usageLabel.toLowerCase()} disponible pour cette période.`
                : "Aucun véhicule disponible pour cette période."}
            </span>
            {available.length > 0 && filterByRequest && (
              <button
                className="text-h3 font-medium text-[#5C6BFF] hover:underline"
                onClick={() => setFilterByRequest(false)}
              >
                Voir tous les véhicules ({available.length})
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-1 max-h-[400px] overflow-y-auto">
            {filteredVehicles.map((v) => (
              <button
                key={v.id}
                disabled={!!assigning}
                onClick={() => handleAssign(v)}
                className="flex flex-row items-center gap-3 p-3 rounded-lg border border-neutral-200 hover:border-[#5C6BFF] hover:bg-[#F5F6FF] transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                  <Icon1
                    name={v.usage === "velos" ? "tool" : "car"}
                    size={18}
                    color="#A1A1A1"
                  />
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-h2 font-medium text-black truncate">{v.name}</span>
                  <span className="text-h4 text-neutral-400">
                    {v.plate} · {v.marque || ""} {v.modele || ""} · {v.km} km
                  </span>
                </div>
                <span
                  className="text-h4 font-medium rounded-full px-2 py-0.5"
                  style={{
                    background: v.usage === "location" ? "#FEF3C7" : v.usage === "velos" ? "#DBEAFE" : "#D1FAE5",
                    color: v.usage === "location" ? "#92400E" : v.usage === "velos" ? "#1E40AF" : "#065F46",
                  }}
                >
                  {v.usage === "location" ? "Location" : v.usage === "velos" ? "Vélo" : "Prêt"}
                </span>
                {assigning === v.id && (
                  <div className="w-4 h-4 border-2 border-[#5C6BFF] border-t-transparent rounded-full animate-spin" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-end">
        <SecondaryButton variant="small" onClick={onClose}>Fermer</SecondaryButton>
      </div>
    </BaseModal>
  );
}

// ─── Tracking preview modal (phone frame) ─────────────────

export function TrackingPreviewModal({
  ordre,
  onClose,
}: {
  ordre: Ordre;
  parentDossier?: Dossier | null;
  orgData?: any;
  onClose: () => void;
}) {
  const [hoveredStar, setHoveredStar] = useState(0);
  const [selectedRating, setSelectedRating] = useState(0);
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getTrackingData(ordre.id)
      .then((d) => { setTrackingData(d); setSelectedRating(d.client_rating || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ordre.id, ordre.status, ordre.tracking_step, ordre.acompte_paid, ordre.solde_paid, ordre.devis_signed]);

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col border rounded-[15px] overflow-hidden shadow-xl" style={{ width: 380, maxHeight: "calc(100vh - 48px)", background: "#FFFFFF", borderColor: "#D9D9D9" }}>
      <div className="flex flex-row items-center justify-between px-[20px] py-[12px] border-b border-[#E5E5E5]">
        <div className="flex flex-col">
          <span className="text-h3 text-neutral-400">Aperçu client</span>
          <span className="text-h2 font-medium" style={{ color: "#5C6BFF" }}>
            Suivi #{ordre.or_number || ordre.num}
          </span>
        </div>
        <button onClick={onClose} className="hover:bg-neutral-100 rounded-full p-1">
          <svg width={12} height={12} viewBox="0 0 12 12" fill="none">
            <path d="M1 1L11 11M11 1L1 11" stroke="#A1A1A1" strokeWidth={1.5} strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-5 h-5 border-2 border-[#5C6BFF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : trackingData ? (
          <TrackingContentV2
            data={trackingData}
            ordreId={ordre.id}
            hoveredStar={hoveredStar}
            setHoveredStar={setHoveredStar}
            selectedRating={selectedRating}
            setSelectedRating={setSelectedRating}
            previewMode
            onDataRefresh={() => {
              getTrackingData(ordre.id)
                .then((d) => { setTrackingData(d); setSelectedRating(d.client_rating || 0); })
                .catch(() => {});
            }}
          />
        ) : (
          <div className="flex items-center justify-center py-12">
            <span className="text-sm text-gray-400">Impossible de charger l'aperçu</span>
          </div>
        )}
      </div>
    </div>
  );
}
